// The header's VIN edit (2026-10-21): the pure text and state behind
// VinEdit.jsx, so they are unit-tested without a React harness. The VIN
// check itself is shared with the server (@cxhub/shared's vinShape() and
// vinCheckDigitOk()), so the box and POST
// /api/ai/conversations/:id/vin-override agree on what a VIN is.
import { vinShape, vinCheckDigitOk } from '@cxhub/shared'

export const VIN_LENGTH = 17
export const VIN_IOQ_MESSAGE = 'VINs never contain I, O or Q'
export const VIN_VALID_MESSAGE = 'Valid VIN'
export const VIN_CHECK_DIGIT_WARNING = "This VIN doesn't pass the check digit. Most US vehicles must; recheck it against the photo or title."
export const VIN_CONFIRM_QUESTION = 'This VIN fails the check digit. Save it anyway?'
export const VIN_UPDATED_NOTICE = 'VIN updated. Regenerate the suggestion to use it.'

// What the box holds as the agent types: uppercased, spaces and dashes (and
// anything else that is not a letter or digit) dropped, at most 17. I, O and
// Q stay, so the helper can say why they are wrong.
export function normalizeVinInput(text) {
  return String(text ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, VIN_LENGTH)
}

// The live helper under the box. lines: [{ text, tone }] with tone
// 'neutral' | 'error' | 'success' | 'warning'. canSave: a 17-character VIN
// shape (a failed check digit can still be saved, after a confirm).
export function vinEditHelp(text) {
  const value = normalizeVinInput(text)
  const lines = [{ text: `${value.length} of ${VIN_LENGTH} characters`, tone: 'neutral' }]
  if (/[IOQ]/.test(value)) lines.push({ text: VIN_IOQ_MESSAGE, tone: 'error' })
  const vin = value.length === VIN_LENGTH ? vinShape(value) : null
  if (!vin) return { value, lines, canSave: false, checkDigitOk: null }
  const checkDigitOk = vinCheckDigitOk(vin)
  lines.push(checkDigitOk ? { text: VIN_VALID_MESSAGE, tone: 'success' } : { text: VIN_CHECK_DIGIT_WARNING, tone: 'warning' })
  return { value: vin, lines, canSave: true, checkDigitOk }
}

export function firstNameOf(name) {
  const first = String(name ?? '').trim().split(/\s+/)[0]
  return first || 'an agent'
}

// The chip beside an edited VIN, and its tooltip.
export function vinEditedChip(vinOverride, fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '')) {
  if (!vinOverride?.vin) return null
  const failed = vinOverride.checkDigitOk === false
  return {
    label: `Edited by ${firstNameOf(vinOverride.setBy)}${failed ? ' · check digit failed' : ''}`,
    color: failed ? 'warning' : 'default',
    tooltip: `Changed from ${vinOverride.previous ?? 'none'} by ${vinOverride.setBy ?? 'an agent'}, ${fmt(vinOverride.setAt)}`,
  }
}

// One Activity Log line per entry of metadata.vinEdits.
export function vinEditActivityLine(edit) {
  if (!edit) return null
  return `VIN changed from ${edit.from ?? 'none'} to ${edit.to ?? 'none'} by ${edit.by ?? 'someone'}${edit.checkDigitOk === false ? ' (check digit failed)' : ''}`
}

// The vehicle a decode names, the same text the server's
// decodedVehicleText() builds (server/src/ai/image-label-read.js).
export function decodedVehicleText(result) {
  if (!result) return null
  const parts = [result.year, result.make, result.model].filter(Boolean)
  if (parts.length > 1) return parts.join(' ')
  if (result.vehicleDescription) return [result.year, result.vehicleDescription].filter(Boolean).join(' ')
  return parts.join(' ') || null
}

// The header fields a vin-override response replaces on the conversation.
export function vinFieldsFromResponse(res) {
  return {
    vin: res?.vin ?? null,
    vinDecodeStatus: res?.vinDecodeStatus ?? null,
    vinDecodeResult: res?.vinDecodeResult ?? null,
    vinOverride: res?.vinOverride ?? null,
  }
}
